import { Container } from '@/components';
import Table from '@/components/common/table/Table';
import { usePaymentReminder } from '@/context/paymentReminderContext/PaymentReminderContextProvider';
import React, { memo, useCallback, useMemo } from 'react';
import { showAlert } from '@/components';
import FastInput from '@/components/common/inputs/FastInput';

// Define email regex once
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const className = 'w-full border p-1 rounded-lg text-black';

// Memoized components for rendering
const EmailInput = memo(({ value, onChange }) => {
  const isValidEmail = value && EMAIL_REGEX.test(value);
  return (
    <div className="flex flex-col items-center">
      <FastInput
        name="email"
        value={value || ''}
        className={className}
        onChange={onChange}
      />
      {value && !isValidEmail && (
        <span className="text-xs text-red-600 mt-1 text-center">
          Invalid email format
        </span>
      )}
    </div>
  );
});

const Email1Input = memo(({ value, onChange }) => {
  const isValidEmail = value && EMAIL_REGEX.test(value);
  return (
    <div className="flex flex-col items-center">
      <FastInput
        name="email1"
        value={value || ''}
        className={className}
        onChange={onChange}
      />
      {value && !isValidEmail && (
        <span className="text-xs text-red-600 mt-1 text-center">
          Invalid email format
        </span>
      )}
    </div>
  );
});

const EmailCheckbox = memo(({ file, checked, onChange }) => {
  const isEmailValid =
    file.customer.email && EMAIL_REGEX.test(file.customer.email);
  return (
    <label
      className="flex justify-center items-center gap-2"
      style={{ marginLeft: 8 }}
    >
      <input
        type="checkbox"
        disabled={!isEmailValid}
        checked={checked}
        onChange={onChange}
      />
      Email
    </label>
  );
});

const Email1Checkbox = memo(({ file, checked, onChange }) => {
  const isEmail1Valid =
    file.customer.email1 && EMAIL_REGEX.test(file.customer.email1);
  return (
    <label
      className="flex justify-center items-center gap-2"
      style={{ marginLeft: 8 }}
    >
      <input
        type="checkbox"
        disabled={!isEmail1Valid}
        checked={checked}
        onChange={onChange}
      />
      Email1
    </label>
  );
});

const CustomerEmailTable = ({ selectedOption }) => {
  const {
    singleFileData,
    isLoading,
    setSingleFileData,
    paymentReminder,
    toggleFileLogSelection,
    updateFileLogEmailInPaymentReminder,
  } = usePaymentReminder();

  // Memoize fileLogsWithIdx
  const fileLogsWithIdx = useMemo(() => {
    return (singleFileData?.fileLogs || []).map((row, idx) => ({
      ...row,
      rowIdx: idx,
    }));
  }, [singleFileData?.fileLogs]);

  // Memoize allSelected state
  const allSelected = useMemo(() => {
    return (
      paymentReminder?.file?.fileLogs?.length ===
        (fileLogsWithIdx?.length || 0) && (fileLogsWithIdx?.length || 0) > 0
    );
  }, [paymentReminder?.file?.fileLogs, fileLogsWithIdx]);

  // Helper to get checked state for each row - memoized
  const getCheckedEmails = useCallback(
    (file) => {
      const logs = paymentReminder?.file?.fileLogs || [];
      const found = logs.find((log) => log.id === file.id);
      if (!found) return { email: false, email1: false, both: false };
      const hasEmail = !!found.customer?.email && !!file.customer.email;
      const hasEmail1 = !!found.customer?.email1 && !!file.customer.email1;
      return {
        email: hasEmail,
        email1: hasEmail1,
        both: hasEmail && hasEmail1,
      };
    },
    [paymentReminder?.file?.fileLogs],
  );

  // Handler to update email/email1 in singleFileData and paymentReminder.file.fileLogs - memoized
  const handleEmailChange = useCallback(
    (rowIdx, field, newValue) => {
      setSingleFileData((prev) => {
        const updated = { ...prev };
        updated.fileLogs = updated.fileLogs.map((row, idx) =>
          idx === rowIdx
            ? {
                ...row,
                customer: {
                  ...row.customer,
                  [field]: newValue,
                },
              }
            : row,
        );
        return updated;
      });

      // Also update in paymentReminder.file.fileLogs if selected
      const fileLog = singleFileData?.fileLogs?.[rowIdx];
      if (
        fileLog &&
        paymentReminder?.file?.fileLogs?.some((log) => log.id === fileLog.id)
      ) {
        updateFileLogEmailInPaymentReminder(fileLog.id, field, newValue);
      }
    },
    [
      setSingleFileData,
      singleFileData?.fileLogs,
      paymentReminder?.file?.fileLogs,
      updateFileLogEmailInPaymentReminder,
    ],
  );

  // Update selection for a row - memoized
  const handleEmailCheckboxChange = useCallback(
    (file, type, checked) => {
      const logs = paymentReminder?.file?.fileLogs || [];
      const isSelected = logs.some((log) => log.id === file.id);
      // Remove any existing selection for this file
      if (isSelected) {
        toggleFileLogSelection(file, 'remove');
      }

      let customer = { ...file.customer };
      if (type === 'email') {
        customer = {
          ...file.customer,
          email: checked ? file.customer.email : undefined,
          email1: getCheckedEmails(file).email1
            ? file.customer.email1
            : undefined,
        };
      } else if (type === 'email1') {
        customer = {
          ...file.customer,
          email: getCheckedEmails(file).email ? file.customer.email : undefined,
          email1: checked ? file.customer.email1 : undefined,
        };
      } else if (type === 'both') {
        customer = {
          ...file.customer,
          email: checked ? file.customer.email : undefined,
          email1: checked ? file.customer.email1 : undefined,
        };
      }

      const isEmailValid = customer.email && EMAIL_REGEX.test(customer.email);
      const isEmail1Valid =
        customer.email1 && EMAIL_REGEX.test(customer.email1);

      if (isEmailValid || isEmail1Valid) {
        toggleFileLogSelection({ ...file, customer }, 'add');
      }
    },
    [paymentReminder?.file?.fileLogs, toggleFileLogSelection, getCheckedEmails],
  );

  // Select all with valid emails or clear all if already selected - memoized
  const handleSelectAll = useCallback(() => {
    if (!fileLogsWithIdx.length) return;

    if (allSelected) {
      // Deselect all
      fileLogsWithIdx.forEach((file) => {
        if (
          paymentReminder?.file?.fileLogs?.some((log) => log.id === file.id)
        ) {
          toggleFileLogSelection(file);
        }
      });
      return;
    }

    const validLogs = [];
    const invalidLogs = [];
    fileLogsWithIdx.forEach((file) => {
      const isEmailValid =
        file.customer.email && EMAIL_REGEX.test(file.customer.email);
      const isEmail1Valid =
        file.customer.email1 && EMAIL_REGEX.test(file.customer.email1);
      if (isEmailValid || isEmail1Valid) {
        validLogs.push(file);
      } else {
        invalidLogs.push(file);
      }
    });

    // Add all valid logs to paymentReminder
    validLogs.forEach((file) => {
      let customer = {};
      if (file.customer.email && EMAIL_REGEX.test(file.customer.email)) {
        customer.email = file.customer.email;
      }
      if (file.customer.email1 && EMAIL_REGEX.test(file.customer.email1)) {
        customer.email1 = file.customer.email1;
      }
      toggleFileLogSelection({ ...file, customer }, 'add');
    });

    if (invalidLogs.length > 0) {
      showAlert(
        'error',
        `${invalidLogs.length} row(s) have invalid email(s) and were not selected.`,
      );
    }
  }, [
    fileLogsWithIdx,
    allSelected,
    paymentReminder?.file?.fileLogs,
    toggleFileLogSelection,
  ]);

  // Determine if Send Mail button should be disabled - memoized
  const isSendMailDisabled = useMemo(() => {
    return (
      !paymentReminder?.template?.id ||
      paymentReminder?.template?.id === 0 ||
      paymentReminder?.emailSender?.id === 0 ||
      !paymentReminder?.emailSender?.id ||
      !paymentReminder?.file?.fileLogs ||
      paymentReminder?.file?.fileLogs.length === 0
    );
  }, [
    paymentReminder?.template?.id,
    paymentReminder?.emailSender?.id,
    paymentReminder?.file?.fileLogs,
  ]);

  // Memoize columns definition
  const columns = useMemo(() => {
    return [
      { header: 'Cust Code', field: 'customer.customerCode' },
      { header: 'Customer Name', field: 'customer.customerName' },
      { header: 'Total', field: 'totalAmount' },
      ...(selectedOption === 'all' || selectedOption === '60'
        ? [{ header: '60', field: 'amount60' }]
        : []),
      ...(selectedOption === 'all' || selectedOption === '30'
        ? [{ header: '30', field: 'amount30' }]
        : []),
      ...(selectedOption === 'all' || selectedOption === '90'
        ? [{ header: '90', field: 'amount90' }]
        : []),
      ...(selectedOption === '60-90'
        ? [
            { header: '60', field: 'amount60' },
            { header: '90', field: 'amount90' },
          ]
        : []),
      {
        header: 'Email',
        render: (file) => (
          <div>
            <EmailInput
              value={file.customer.email}
              onChange={(e) =>
                handleEmailChange(file.rowIdx, 'email', e.target.value)
              }
            />
            <EmailCheckbox
              file={file}
              checked={getCheckedEmails(file).email}
              onChange={(e) =>
                handleEmailCheckboxChange(file, 'email', e.target.checked)
              }
            />
          </div>
        ),
      },
      {
        header: 'Email 1',
        render: (file) => (
          <div>
            <Email1Input
              value={file.customer.email1}
              onChange={(e) =>
                handleEmailChange(file.rowIdx, 'email1', e.target.value)
              }
            />
            <Email1Checkbox
              file={file}
              checked={getCheckedEmails(file).email1}
              onChange={(e) =>
                handleEmailCheckboxChange(file, 'email1', e.target.checked)
              }
            />
          </div>
        ),
      },
      {
        header: 'Select',
        render: (file) => {
          const disabled =
            !(file.customer.email && EMAIL_REGEX.test(file.customer.email)) &&
            !(file.customer.email1 && EMAIL_REGEX.test(file.customer.email1));
          return (
            <label className="gap-2">
              <input
                type="checkbox"
                disabled={disabled}
                checked={getCheckedEmails(file).both}
                onChange={(e) =>
                  handleEmailCheckboxChange(file, 'both', e.target.checked)
                }
              />
              Both
            </label>
          );
        },
      },
    ];
  }, [
    selectedOption,
    handleEmailChange,
    getCheckedEmails,
    handleEmailCheckboxChange,
  ]); // Dependencies for columns

  return (
    <Container>
      <div
        className="m-5"
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginBottom: 8,
          gap: 12,
        }}
      >
        <button
          type="button"
          className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
          onClick={handleSelectAll}
          style={{ marginRight: 8 }}
        >
          Select All
        </button>
        <button
          type="button"
          className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600"
          onClick={() => {
            // Deselect all
            fileLogsWithIdx.forEach((file) => {
              if (
                paymentReminder?.file?.fileLogs?.some(
                  (log) => log.id === file.id,
                )
              ) {
                toggleFileLogSelection(file);
              }
            });
          }}
        >
          Unselect All
        </button>
      </div>
      <Table
        columns={columns}
        data={fileLogsWithIdx}
        isLoading={isLoading}
        // useVirtualScroll={true}
        // rowHeight={50}
        // viewportHeight={2000}
        isForSorting={false}
      />
    </Container>
  );
};

export default CustomerEmailTable;
